import { useFormikContext } from 'formik';
import { useMemo } from 'react';

import './CustomerLeadStepFieldsTab.scss';

import { TextInputField } from 'src/components/base';
import { CollapsedFields } from 'src/components/base/CollapsedFields/CollapsedFields';
import { SpecificationField } from 'src/components/base/fields/SpecificationField/SpecificationField';
import type { ICustomerLeadProductSpecificationDataFormValues } from 'src/components/customers/CustomerLeadTypes';
import { useDebounceEffect } from 'src/hooks/useDebounceEffect';
import { useOldSpecificationsFieldsView } from 'src/hooks/useOldSpecificationsFieldsView';
import type { ICustomerLeadProductSpecificationData } from 'src/types/customers/ICustomerLeadProductSpecificationData';
import type { IProductSpecificationReference } from 'src/types/reference/IProductSpecificationReference';
import { ProductSpecificationFieldGroup } from 'src/types/reference/ProductSpecificationFieldGroup';

type CustomerLeadStepDescriptionTabProps<T extends ICustomerLeadProductSpecificationDataFormValues> = {
  specification: IProductSpecificationReference;
  fieldGroup: ProductSpecificationFieldGroup;

  onChange: (newValues: T) => void;
};

export function CustomerLeadStepFieldsTab<T extends ICustomerLeadProductSpecificationDataFormValues>({
  specification,
  fieldGroup,
  onChange,
}: Readonly<CustomerLeadStepDescriptionTabProps<T>>) {
  const isOldView = useOldSpecificationsFieldsView();

  const { values, validateForm } = useFormikContext<T>();

  const fieldsForGroup = useMemo(
    () => specification.fields.filter((field) => field.group === fieldGroup),
    [specification.fields, fieldGroup]
  );

  // Check changed values only for current Tab
  const autoSaveKey = useMemo(
    () =>
      fieldsForGroup
        .reduce(
          (acc, curr) => {
            acc.push(`${curr.id}<->${JSON.stringify(values.specData[curr.id])}`);

            return acc;
          },
          [] as ICustomerLeadProductSpecificationData[string][]
        )
        .sort((a, b) => (a == null ? -1 : b == null ? 1 : a === b ? 0 : a > b ? 1 : -1))
        .join('|'),
    [fieldsForGroup, values.specData]
  );

  useDebounceEffect(
    () => {
      void (async () => {
        const errors = await validateForm(values);

        if (Object.keys(errors).length === 0) {
          onChange(values);
        }
      })();
    },
    [autoSaveKey],
    1000
  );

  return (
    <div className="customer-lead-step-fields-tab__container">
      {isOldView ? (
        <>
          {fieldGroup === ProductSpecificationFieldGroup.Quantities && (
            <TextInputField type="number" name="initialQty" label="Initial Quantity" showControlIcons={true} />
          )}
          {fieldsForGroup.map((field) => (
            <SpecificationField
              key={field.id}
              field={field}
              specification={specification}
              specificationData={values.specData}
            />
          ))}
        </>
      ) : (
        <CollapsedFields
          fieldGroup={fieldGroup}
          fieldsForGroup={fieldsForGroup}
          specification={specification}
          specData={values.specData}
        />
      )}
    </div>
  );
}
